import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, ExternalLink, FileText, Info } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import JobStatusBadge from "@/components/JobStatusBadge";
import SubmittedReport from "@/components/SubmittedReport";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { formatDate } from "@/lib/format";

/**
 * Standalone report route.
 * - /jobs/:id/report — office/manager/super_admin (backend enforces).
 * - /my/jobs/:id/report — engineer (own jobs; enforced by scope filter).
 * Route param decides the back link only; permission is server-side.
 */
export default function JobReportPage({ scope = "office" }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api.get(`/jobs/${id}`)
      .then(({ data }) => { if (mounted) setJob(data); })
      .catch((err) => {
        toast.error(formatApiError(err));
        navigate(scope === "engineer" ? "/my/jobs" : "/jobs", { replace: true });
      })
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [id, navigate, scope]);

  if (loading) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  if (!job) return null;

  const backTo = scope === "engineer" ? "/my/jobs" : "/jobs";
  const isJustCompleted = job.status === "completed";
  const isClosed = job.status === "closed";

  return (
    <div className="p-4 sm:p-6 space-y-4 max-w-5xl mx-auto" data-testid="job-report-page">
      <div className="flex items-center gap-2">
        <Link to={backTo} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="job-report-back">
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </Link>
      </div>
      <PageHeader
        testId="job-report-header"
        title={<span className="flex items-center gap-2"><span className="font-mono text-base">{job.job_number}</span><span className="truncate">{job.title}</span></span>}
        subtitle={`${job.customer_name} · ${job.site_name}`}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <JobStatusBadge status={job.status} />
            {isClosed && job.report_pdf_file_id && (
              <Button asChild size="sm" variant="outline" data-testid="job-report-download-pdf">
                <a
                  href={`/api/jobs/${job.id}/report.pdf`}
                  target="_blank" rel="noopener noreferrer"
                >
                  <FileText className="h-4 w-4" /> Download PDF <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </Button>
            )}
          </div>
        }
      />

      {/* Bug 8e handoff banner for engineers */}
      {scope === "engineer" && (
        <Card className="rounded-md border-primary/40 bg-primary/5" data-testid="job-report-banner">
          <CardContent className="p-4 flex items-start gap-3">
            <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <div className="font-medium">Submitted{job.completed_at ? ` at ${formatDate(job.completed_at)}` : ""}. This report is now read-only.</div>
              {isJustCompleted && <div className="text-xs text-muted-foreground mt-0.5">Waiting on office to close and email the customer.</div>}
              {isClosed && <div className="text-xs text-muted-foreground mt-0.5">Closed{job.email_sent_at ? `; report emailed to customer ${formatDate(job.email_sent_at)}` : ""}.</div>}
            </div>
          </CardContent>
        </Card>
      )}

      {scope === "office" && isClosed && (
        <Card className="rounded-md bg-muted/40" data-testid="job-report-office-banner">
          <CardContent className="p-3 text-sm flex items-start gap-2">
            <Info className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
            <div>
              Closed and archived{job.email_sent_at ? `; emailed ${formatDate(job.email_sent_at)}` : ""}.
              {job.email_error && <span className="text-destructive"> Email issue: {job.email_error}</span>}
            </div>
          </CardContent>
        </Card>
      )}

      <SubmittedReport job={job} />
    </div>
  );
}
